import { DaySummary, EVENT_PILL_CLASS } from '../lib/daySummary';

interface CalendarDayCellProps {
  dateKey: string; // "YYYY-MM-DD"
  isCurrentMonth: boolean;
  summary: DaySummary | undefined;
  isToday: boolean;
  isSelected: boolean;
  dow: number; // 0=일
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  /** 셀 높이 클래스 (월간: 반응형 min-h, 주간: 고정 행 높이 채움) */
  sizeClass?: string;
  /** 주간 뷰 월 경계 배경 톤 (비선택 셀의 기본 배경 override) */
  toneClass?: string;
  /** 주간 뷰에서 매월 1일에 "N월" 라벨 표시 */
  monthLabel?: string | null;
}

/** 월간/주간 달력 공용 날짜 셀 (날짜 번호 + 변경요청 배지 + 담당자 분할 배지) */
export default function CalendarDayCell({
  dateKey,
  isCurrentMonth,
  summary,
  isToday,
  isSelected,
  dow,
  onClick,
  sizeClass = 'min-h-[48px] sm:min-h-[80px] lg:min-h-[100px]',
  toneClass,
  monthLabel = null,
}: CalendarDayCellProps) {
  const dayNum = parseInt(dateKey.split('-')[2], 10);
  const hasInspections = summary && summary.total > 0;
  const hasPendingRequest = summary?.hasPendingRequest ?? false;
  const visibleEvents = summary?.events.slice(0, 3) ?? [];
  const extraCount = (summary?.events.length ?? 0) - 3;
  const statusCounts = summary?.statusCounts ?? { scheduled: 0, inProgress: 0, completed: 0 };

  const bgClass =
    isSelected && isCurrentMonth
      ? 'bg-gradient-to-b from-brand-50 to-white ring-2 ring-brand-400 ring-inset'
      : !isCurrentMonth
      ? 'bg-surface-soft/70 hover:bg-surface-hover/70'
      : toneClass
      ? `${toneClass} hover:bg-surface-hover/70`
      : 'hover:bg-surface-soft';

  return (
    <button
      data-calendar-cell
      onClick={onClick}
      className={`${sizeClass} border-b border-r border-line-soft last:border-r-0 p-1 sm:p-1.5 flex flex-col justify-start items-start transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-inset ${bgClass}`}
    >
      {/* 날짜 번호 */}
      <div className="flex items-start justify-between w-full mb-0.5 sm:mb-1">
        <span className="flex items-center gap-1 min-w-0">
          <span
            className={`text-xs tabular-nums w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center rounded-full flex-shrink-0 ${
              isToday && isCurrentMonth
                ? 'bg-brand-600 font-bold text-white shadow-[0_4px_10px_-2px_rgba(79,70,229,0.4)]'
                : !isCurrentMonth
                ? 'font-semibold text-ink-300'
                : dow === 0
                ? 'font-semibold text-rose-500'
                : dow === 6
                ? 'font-semibold text-blue-500'
                : 'font-semibold text-ink-700'
            }`}
          >
            {dayNum}
          </span>
          {monthLabel && (
            <span className="text-[10px] font-semibold text-ink-500 truncate">{monthLabel}</span>
          )}
        </span>
        {/* 변경 요청 대기 배지 */}
        {hasPendingRequest && (
          <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-amber-400 ring-2 ring-amber-400/25 flex-shrink-0 mt-1" title="변경 요청 대기 중" />
        )}
      </div>

      {/* 모바일 (sm 미만): 완료건/전체건 텍스트 */}
      {hasInspections && (
        <div className={`sm:hidden w-full flex justify-center ${!isCurrentMonth ? 'opacity-50' : ''}`}>
          <span
            className={`text-[10px] font-semibold leading-tight ${
              statusCounts.completed === summary!.total
                ? 'text-emerald-600'
                : 'text-ink-500'
            }`}
          >
            {statusCounts.completed}/{summary!.total}
          </span>
        </div>
      )}

      {/* 폴드/PC (sm 이상): 점검 1건 = 이벤트 pill 1개 (목업 `.ev` — 연배경 + 점 + 담당자 이름).
          점은 currentColor라 상태색을 pill 클래스 한 곳만 바꾸면 같이 따라온다. */}
      {hasInspections && (
        <div className={`hidden sm:flex min-w-0 w-full flex-col gap-1 ${!isCurrentMonth ? 'opacity-50' : ''}`}>
          {visibleEvents.map((ev, i) => (
            // 배지 갈래 6px — `Badge` 사각형이 소유한 값과 동일(목업 .ev radius 6px)
            // eslint-disable-next-line local/no-untokenized-radius
            <span
              key={`${ev.name}-${i}`}
              className={`flex min-w-0 w-full items-center gap-[5px] rounded-[6px] px-2 py-1 text-[10px] lg:text-[11.5px] font-semibold tracking-[-0.01em] ${
                EVENT_PILL_CLASS[ev.status] ?? 'bg-ink-100 text-ink-600'
              }`}
            >
              <span aria-hidden="true" className="h-[5px] w-[5px] flex-none rounded-full bg-current opacity-[0.65]" />
              <span className="truncate">{ev.name}</span>
            </span>
          ))}
          {extraCount > 0 && (
            // 목업 `.ev-more` — radius 5px, soft 바탕의 건수 칩
            // eslint-disable-next-line local/no-untokenized-radius
            <span className="self-start rounded-[5px] bg-surface-soft px-1.5 py-0.5 text-[10px] lg:text-[11px] font-bold tracking-[.01em] text-ink-500">
              +{extraCount}건
            </span>
          )}
        </div>
      )}
    </button>
  );
}
